import React, { useState, useEffect, useRef } from "react";
import { useAuthContext } from "../../contexts/Auth-Context";
import { Form, useNavigate } from "react-router-dom";
import APIMiddleware from "../../data/api/Api-Middleware";
import { toast } from "react-toastify";
import { useLocation } from 'react-router-dom';

// import logo from "../../imgs/logo.png";
import {
  API_ENDPOINT_CHECK_EMAIL,
  API_ENDPOINT_DIRECT_LOGIN,
  API_ENDPOINT_LOGIN,
  API_ENDPOINT_USER_DETAILS,
  API_ENDPOINT_GET_REGISTER_URL,
} from "../../data/Endpoints-API";
import "./Login.scss";
import VariantModal from "./VariantLogout";
import getBackendUrl, { getBackendPic } from "../../components/utils/RedirectUrl";
import { useMetricsContext } from "../../contexts/Metrics-Context";
import { logClientManualAction, resolveClientPublicIp } from "../../utils/manualActionAudit";
import { getOrCreateSessionVariantId, persistSessionVariantId } from "../../utils/sessionVariantId";
import Loader from "../../components/utils/Loader/Loader.jsx";
import { clearChallengeSwitchContext } from "../../utils/challengeSwitchContext";
import {
  applyLoginShellStyles,
  DIRECT_LOGIN_PROGRESS_KEY,
  hasDirectLoginTokenInUrl,
} from "../../utils/directLoginBootstrap";
import { useTranslation } from "react-i18next";
import LanguageSelect from "../../components/utils/LanguageSelect.jsx";
import { normalizeTzParam } from "../../utils/timezone.js";
import { localizeApiMessage } from "../../i18n/apiDisplayMessage.js";

const stripDirectLoginTokenFromUrl = () => {
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('token') && !url.searchParams.has('tz') && !url.searchParams.has('timezone')) return;
    url.searchParams.delete('token');
    url.searchParams.delete('tz');
    url.searchParams.delete('timezone');
    window.history.replaceState({}, '', url.pathname + url.search + url.hash);
  } catch (_) {
    // no-op
  }
};

const Login = () => {
  const [inputActiveEmail, setInputActiveEmail] = useState('free');
  const [inputActivePass, setInputActivePass] = useState('free');

  let p_u_email, p_u_password;
  if (localStorage.userData != undefined) {
    const previousUser = JSON.parse(localStorage.userData);
    p_u_email = previousUser.email;
    p_u_password = previousUser.password;
    // inputActive = true;
    // setInputActiveEmail('filled');
    // setInputActivePass('filled');
  }
  const navigate = useNavigate();
  const { login, logoutWithOutRefresh, isMariginCallVisible, setIsMariginCallVisible } = useAuthContext();
  const { setUTCOffset } = useMetricsContext();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  // const [loginError, setLoginError] = useState("");

  const [showPasswordInput, setShowPasswordInput] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [allUserOptions, setAllUserOptions] = useState([]);
  const [selectedUserAccount, setSelectedUserAccount] = useState(null);
  const [selectedUserUniqueId, setSelectedUserUniqueId] = useState(null);
  const [checkEmail, setCheckEmail] = useState(false);

  const [readyStateRole, setReadyStateRole] = useState("wait");
  const [passwordEyeState, setPasswordEyeState] = useState("password");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isRegisterLoading, setIsRegisterLoading] = useState(false);
  const [isDirectLoginPending, setIsDirectLoginPending] = useState(() => hasDirectLoginTokenInUrl());
  const { t } = useTranslation();

  /** HTML5 tooltips follow the browser UI language unless we set customValidity. */
  const setRequiredValidity = (el) => {
    el.setCustomValidity(el.validity.valueMissing ? t('common.requiredField') : '');
  };

  const setEmailValidity = (el) => {
    if (el.validity.valueMissing) {
      el.setCustomValidity(t('common.requiredField'));
    } else if (el.validity.typeMismatch) {
      el.setCustomValidity(t('common.emailMissingAt', { email: el.value }));
    } else {
      el.setCustomValidity('');
    }
  };

  /** Accedi submit stays disabled until account+password exist; still show tooltips. */
  const reportLoginFieldValidity = () => {
    const emailEl = emailInputRef.current;
    if (emailEl) {
      setEmailValidity(emailEl);
      if (!emailEl.checkValidity()) {
        emailEl.reportValidity();
        return false;
      }
    }
    if (showPasswordInput) {
      const passEl = passwordInputRef.current;
      if (passEl) {
        setRequiredValidity(passEl);
        if (!passEl.checkValidity()) {
          passEl.reportValidity();
          return false;
        }
      }
    }
    return true;
  };

  // REFS
  const passwordInputRef = useRef(null);
  const emailInputRef = useRef(null);
  const readyStateEl = useRef(null);
  const dropdownRef = useRef(null);
  
  const location = useLocation();

  useEffect(() => {
    void resolveClientPublicIp();
  }, []);

  useEffect(() => {
    if (showPasswordInput && passwordInputRef.current) {
      passwordInputRef.current.focus();
    }
  }, [showPasswordInput]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (!params.has('token')) {
      return;
    }

    applyLoginShellStyles();
    sessionStorage.setItem(DIRECT_LOGIN_PROGRESS_KEY, '1');

    const token = params.get('token');

    const tzParam = params.get('tz') || params.get('timezone');
    if (tzParam) {
      const normalized = normalizeTzParam(tzParam);
      if (normalized) {
        localStorage.setItem('utc_time', normalized);
        setUTCOffset(normalized);
      }
    }

    directLogin(token);
  }, [location]);

  const directLogin = async (token) => {
    if (!token) return;

    setIsDirectLoginPending(true);
    sessionStorage.setItem(DIRECT_LOGIN_PROGRESS_KEY, '1');
    applyLoginShellStyles();

    try {
      if (localStorage.variantId) {
        await logoutWithOutRefresh(localStorage.variantId);
      }
      localStorage.removeItem('userId');
      localStorage.removeItem('token');

      await directLoginApi({ token });
    } finally {
      sessionStorage.removeItem(DIRECT_LOGIN_PROGRESS_KEY);
      setIsDirectLoginPending(false);
    }
  };


  const handleLogin = async (e) => {
    e.preventDefault(); // Prevent the default form submission behavior

    if (!reportLoginFieldValidity()) {
      return;
    }

      if (email && password && selectedUserUniqueId && !isSubmitting) {
        setIsSubmitting(true);

        const data = {
          email,
          password,
          unique_id: selectedUserUniqueId
        };
        loginApi(data)

      }
  };

  const directLoginApi = async (data) =>{
    try{
      clearChallengeSwitchContext();
      const response = await APIMiddleware.post(API_ENDPOINT_DIRECT_LOGIN(), data);

      if (response && response.data[0]) {
        const userData = { email: email, password: password };
        const jsonUd = JSON.stringify(userData);
        localStorage.setItem('userData', jsonUd);
        localStorage.setItem("userId", response.data[0].userId);
        localStorage.setItem("token", response.token);
        localStorage.setItem("accountType", response.accountType);
        localStorage.setItem("serverUrl", response.data[0]?.server_url);

        const sessionVariantId = getOrCreateSessionVariantId({ forceNew: true });
        persistSessionVariantId(sessionVariantId);
        void logClientManualAction('DIRECT_LOGIN', {
          token_flow: true,
          source: 'trade-client',
        });

        stripDirectLoginTokenFromUrl();
        navigate("/home", { replace: true });

        if (response.data[0].liquidation_warning == 1) {
          setIsMariginCallVisible(true);
        }
      } else {
        stripDirectLoginTokenFromUrl();
        toast.error(t('toasts.invalidLogin'), {
          position: "top-right",
        });
      }
    } catch (error) {
      stripDirectLoginTokenFromUrl();
      console.error(`API request error: ${API_ENDPOINT_DIRECT_LOGIN()}`, error?.response?.status);
      if (error?.response?.status == 500) {
        toast.error(localizeApiMessage(error?.response?.data?.error, t), {
          position: "top-right",
        });
      } else {
        toast.error(t('toasts.sessionExpired'), {
          position: "top-right",
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  const loginApi = async (data) =>{
    try{
      clearChallengeSwitchContext();
      // Extract hostname from backend URL for X-domain header
      const backendUrl = getBackendUrl();
      const domain = new URL(backendUrl).hostname;
      const response = await APIMiddleware.post(API_ENDPOINT_LOGIN(), data, { 'X-domain': domain });
          
      if (response && response.data[0]) {
        // Store the token in localStorage
        //localStorage.setItem('authToken', response.data[0].token);
        const userData = { email: email, password: password };
        const jsonUd = JSON.stringify(userData);
        localStorage.setItem('userData', jsonUd);
        localStorage.setItem("userId", response.data[0].userId);         //for time being
        localStorage.setItem("token", response.token);         //for time being
        localStorage.setItem("accountType", response.accountType);         //for time being
        // document.body.className = `theme-dark`;
        // console.log(response.data[0],"response.data[0]",data)
        // await login(response.data[0]);
  
        const sessionVariantId = getOrCreateSessionVariantId();
        persistSessionVariantId(sessionVariantId);
        localStorage.setItem("serverUrl", response.data[0]?.server_url);
        void logClientManualAction('LOGIN', {
          email,
          unique_id: selectedUserUniqueId,
          source: 'trade-client',
        });
        navigate("/home");

        if (response.data[0].liquidation_warning == 1) {
          setIsMariginCallVisible(true)
        }
      } else {
        toast.error(t('toasts.invalidLogin'), {
          position: "top-right",
        });
        // setLoginError('Invalid login. Please try again.');
      }
    } catch (error) {
      console.error(`API request error: ${API_ENDPOINT_LOGIN()}`, error?.response?.status
      );
      if (error?.response?.status == 500) {
        toast.error(localizeApiMessage(error?.response?.data?.error, t), {
          position: "top-right",
        });
      } else {
        toast.error(t('toasts.genericError'), {
          position: "top-right",
        });
      }

      // setLoginError('An error occurred. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  }

  const [loadOne, setLoadOne] = useState(false);
  // let loadOne = false;
  // const bothOnload = setInterval(() => {
  //   console.log('inside interval');
  //   loadOne = false;
  //   if (document.getElementById("readyState") != null) {
  //     console.log('element not null: inside interval');
  //     loadOne = true;
  //   }
  // }, 500);
  // console.log('render');

  useEffect(() => {
    if (readyStateEl.current != null) {
      setLoadOne(true);
      // console.log('inside useEffect');
    }
  }, [readyStateEl]);

  useEffect(() => {
    // clearInterval(bothOnload)
    document.body.style.display = 'block';
    const timeoutId = setTimeout(() => {
      // document.getElementById("readyState")?.setAttribute("role", "ready");
      setReadyStateRole("ready");
    }, 1000);
    // const gridUp = document.querySelector(".grid-up");
    // const columns =
    //   getComputedStyle(gridUp).gridTemplateColumns.split(" ").length;
    // const rows = getComputedStyle(gridUp).gridTemplateRows.split(" ").length;
    // const blocks = columns * rows;
    // for (let i = 0; i < blocks; i++) {
    //   let blockElement = document.createElement("div");
    //   blockElement.className = "box";
    //   gridUp.append(blockElement);
    // }

    // const gridDown = document.querySelector(".grid-down");
    // const second_columns =
    //   getComputedStyle(gridDown).gridTemplateColumns.split(" ").length;
    // const second_rows =
    //   getComputedStyle(gridDown).gridTemplateRows.split(" ").length;
    // const second_blocks = second_columns * second_rows;
    // for (let i = 0; i < second_blocks; i++) {
    //   let blockElement = document.createElement("div");
    //   blockElement.className = "box";
    //   gridDown.append(blockElement);
    // }

    return () => {
      clearTimeout(timeoutId);
    };
  }, [loadOne]);

  // const inputFocused = (e) => {
  //   e.target.parentNode?.setAttribute("role", "focus");
  // };
  const inputFocusedEmail = () => {
    // e.target.parentNode?.setAttribute("role", "focus");
    setInputActiveEmail('focus');
  };
  const inputFocusedPass = () => {
    setInputActivePass('focus');
  };
  // const inputFree = (e) => {
  //   if (e.target.value.split("").length == 0) {
  //     e.target.parentNode.setAttribute("role", "free");
  //   } else {
  //     e.target.parentNode.setAttribute("role", "filled");
  //   }
  // };
  const inputFreeEmail = () => {
    if (!email) {
      setInputActiveEmail('free');
    } else {
      setInputActiveEmail('filled');
    }
  };
  const inputFreePass = () => {
    if (!password) {
      setInputActivePass('free');
    } else {
      setInputActivePass('filled');
    }
  };

  const passwordEye = (val) => {
    // e.target.parentNode?.setAttribute("type", v);
    // e.target.parentNode?.children[1]?.setAttribute("type", v);
    setPasswordEyeState(val);
  };

  window.addEventListener("contextmenu", (e) => {
    e.preventDefault();
  });

  // window.onmousemove = (e) => {
  //   const heroPointer = document.getElementsByClassName("my-hero-pointer")[0];
  //   if (heroPointer != undefined) {
  //     setTimeout(() => {
  //       heroPointer.style.left = `${e.x + heroPointer.getClientRects()[0]?.width + 4}px`;
  //       heroPointer.style.top = `${e.y + heroPointer.getClientRects()[0]?.height + 6}px`;
  //     }, 100);
  //   }
  // };

  const checkEmailApi = async () => {
    try {
      if (email != '' && email != null && email != undefined && email != 'undefined' && !checkEmail) {
            setCheckEmail(true);
            // Extract hostname from backend URL for X-domain header
            const backendUrl = getBackendUrl();
            const domain = new URL(backendUrl).hostname;
            const response = await APIMiddleware.get(
              API_ENDPOINT_CHECK_EMAIL(email),
              { 'X-domain': domain }
            );
            if (response.status == false && response.count >= 3) {
              setIsModalOpen(true);
            }
            
            if (response.data.length > 1) {
              const allSymbolOptions = Object.entries(response.data).map(([key, value]) => ({
                value: value.unique_id,
                label: value.name + ' - ' + value.unique_id,
              }));
              setShowUserDropdown(true);
              setAllUserOptions(allSymbolOptions);
            }else{
              setSelectedUserUniqueId(response.data[0].unique_id);
              setShowPasswordInput(true);
              setShowUserDropdown(false);
            }
          }
    } catch (error) {
      if (error?.response?.status == 500) {
        toast.error(localizeApiMessage(error?.response?.data?.error, t), {
          position: "top-right",
        });
      setShowUserDropdown(false);
      setShowPasswordInput(false);
      setSelectedUserAccount(null);
      setSelectedUserUniqueId(null);
      } else {
        toast.error(t('toasts.genericError'), {
          position: "top-right",
        });
      }

      console.error(`Error getting user`, error);
    } finally {
      setCheckEmail(false);
    }
  };

  const handleUserOptionChange = (user) => {
    setSelectedUserUniqueId(user.value);
    setSelectedUserAccount(user);
    setShowPasswordInput(true);
    setIsDropdownOpen(false);
    setSearchTerm("");
  }

  // Filter options based on search term
  const filteredOptions = Array.isArray(allUserOptions) && allUserOptions.length > 0
    ? allUserOptions.filter(option => 
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : [];

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedUserUniqueId(null);
    setSelectedUserAccount(null);
    setShowPasswordInput(false);
    setShowUserDropdown(false);
    setEmail('');
    setPassword('');
    setSearchTerm('');
    setIsDropdownOpen(false);
    inputFreeEmail();
    inputFreePass();
  }

  const handleOpenModal = () => {
    setIsModalOpen(false);
  }

  const handleRegisterClick = async (e) => {
    e.preventDefault();
    
    if (isRegisterLoading) {
      return;
    }

    try {
      setIsRegisterLoading(true);
      
      // Get current page URL and remove trailing slash if present
      let currentPageUrl = window.location.href;
      if (currentPageUrl.endsWith('/')) {
        currentPageUrl = currentPageUrl.slice(0, -1);
      }
      console.log('currentPageUrl--------------', currentPageUrl);

      // Extract hostname from backend URL for X-domain header (same as checkEmailApi)
      const backendUrl = getBackendUrl();
      const domain = new URL(backendUrl).hostname;
      console.log('backendUrl--------------', backendUrl);
      console.log('domain (X-domain header)--------------', domain);
      
      // Call the API to get the frontend URL
      const response = await APIMiddleware.get(
        API_ENDPOINT_GET_REGISTER_URL(currentPageUrl),
        { 'X-domain': domain }
      );

      if (response && response.data && response.data.frontend_url) {
        // Concatenate the route with the frontend URL
        const registerUrl = `${response.data.frontend_url}/register_user`;
        // Open in new tab (same as current behavior)
        window.open(registerUrl, '_blank');
      } else {
        toast.error(t('toasts.registrationUrlFailed'), {
          position: "top-right",
        });
      }
    } catch (error) {
      console.error(`Error getting register URL`, error);
      if (error?.response?.status === 400 || error?.response?.status === 404) {
        const errorMessage = error?.response?.data?.error || error?.response?.data?.message || "Domain not found. Please check your configuration.";
        toast.error(errorMessage, {
          position: "top-right",
        });
      } else if (error?.response?.status === 500) {
        toast.error(error?.response?.data?.error || "Unable to get registration URL. Please try again later.", {
          position: "top-right",
        });
      } else {
        toast.error(t('toasts.registrationUrlFailed'), {
          position: "top-right",
        });
      }
    } finally {
      setIsRegisterLoading(false);
    }
  }

  if (isDirectLoginPending) {
    return (
      <div id="mybody" role="login" className="direct-login-pending">
        <Loader />
      </div>
    );
  }

  return (
    <>
    <VariantModal show={isModalOpen} onConfirm={handleOpenModal} onCancel={handleCloseModal} title={t('login.variantsTitle')} message={t('login.variantsMessage')}/>
      <div id="mybody" role="login">
        {/* <div className="my-hero-pointer"></div>
        <div className="bg-animation">
          <div className="top-to-bottom"></div>
        </div>
        <div className="grid-z-two">
          <div className="grid-up grid-box"></div>
          <div className="grid-down grid-box"></div>
        </div> */}
        <div className="login-window">
          <form id="readyState" ref={readyStateEl} role={readyStateRole} onSubmit={handleLogin} noValidate>
          <div className="login-lang-row">
            <LanguageSelect />
          </div>
          <img src={`${getBackendUrl()}/assets/admin/images/logo/${getBackendPic()}logo-full.png`} alt="Logo" />
            <h2>{t('login.welcomeBack')}</h2>
            <p>{t('login.subtitle')}</p>
            <div className="input-cap" role={inputActiveEmail}>
              <p>{t('login.emailLabel')}</p>
              <input
                type="email"
                ref={emailInputRef}
                onBlur={(e) => {
                  // Call your API function here with the email value (optional)
                 !checkEmail&& checkEmailApi(e.target.value); 
                  // inputFree(e);
                  inputFreeEmail();
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    if (!reportLoginFieldValidity()) return;
                    !checkEmail&&  checkEmailApi();
                    inputFreeEmail();
                  }
                }}
                // onFocus={(e) => inputFocused(e)}
                onFocus={() => inputFocusedEmail()}
                // onBlur={(e) => inputFree(e)}
                value={email}
                required
                onInvalid={(e) => setEmailValidity(e.target)}
                onInput={(e) => {
                  // Clear so the next invalid event re-evaluates with the current locale/message.
                  e.target.setCustomValidity('');
                }}
                onChange={(e) => setEmail(e.target.value)}
              />
              <svg viewBox="0 0 22 18" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 1H19C20.1 1 21 1.9 21 3V15C21 16.1 20.1 17 19 17H3C1.9 17 1 16.1 1 15V3C1 1.9 1.9 1 3 1Z" />
                <path d="M21 3L11 10L1 3" />
              </svg>
            </div>
          {showUserDropdown && (
            <div className="custom-account-selector" ref={dropdownRef}>
              <div 
                className="input-cap account-selector-input" 
                role={isDropdownOpen || selectedUserAccount?.value ? "focus" : "free"}
                onClick={() => {
                  if (!isDropdownOpen) {
                    setIsDropdownOpen(true);
                    setSearchTerm("");
                  }
                }}
              >
                <p>{t('login.selectAccount')}</p>
                <input
                  type="text"
                  value={isDropdownOpen ? searchTerm : (selectedUserAccount?.label || "")}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    if (!isDropdownOpen) setIsDropdownOpen(true);
                  }}
                  onFocus={() => {
                    setIsDropdownOpen(true);
                    setSearchTerm("");
                  }}
                  placeholder=""
                />
                <svg 
                  className={`dropdown-arrow ${isDropdownOpen ? 'open' : ''}`}
                  viewBox="0 0 24 24" 
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M7 10L12 15L17 10H7Z" fill="currentColor" />
                </svg>
              </div>
              {isDropdownOpen && (
                <div className="custom-dropdown-menu">
                  {filteredOptions.length > 0 ? (
                    filteredOptions.map((option) => (
                      <div
                        key={option.value}
                        className={`dropdown-option ${
                          selectedUserAccount?.value === option.value ? 'selected' : ''
                        }`}
                        onClick={() => handleUserOptionChange(option)}
                      >
                        {option.label}
                      </div>
                    ))
                  ) : (
                    <div className="dropdown-option no-results">
                      {t('login.noAccountsFound')}
                    </div>
                  )}
                </div>
              )}
            </div>
          )} 
            {showPasswordInput && (
            <div className="input-cap" role={inputActivePass} type={passwordEyeState}>
              <p>{t('login.passwordLabel')}</p>
              <input
                type={passwordEyeState}
                ref={passwordInputRef}
                // onFocus={(e) => inputFocused(e)}
                onFocus={() => inputFocusedPass()}
                // onBlur={(e) => inputFree(e)}
                onBlur={() => inputFreePass()}
                value={password}
                required
                onInvalid={(e) => setRequiredValidity(e.target)}
                onInput={(e) => e.target.setCustomValidity('')}
                onChange={(e) => setPassword(e.target.value)}
              />
              {/* closed eye */}
              <svg
                onClick={() => passwordEye("text")}
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M17.94 17.94C16.2306 19.243 14.1491 19.9649 12 20C5 20 1 12 1 12C2.24389 9.68192 3.96914 7.65663 6.06 6.06003M9.9 4.24002C10.5883 4.0789 11.2931 3.99836 12 4.00003C19 4.00003 23 12 23 12C22.393 13.1356 21.6691 14.2048 20.84 15.19M14.12 14.12C13.8454 14.4148 13.5141 14.6512 13.1462 14.8151C12.7782 14.9791 12.3809 15.0673 11.9781 15.0744C11.5753 15.0815 11.1752 15.0074 10.8016 14.8565C10.4281 14.7056 10.0887 14.4811 9.80385 14.1962C9.51897 13.9113 9.29439 13.572 9.14351 13.1984C8.99262 12.8249 8.91853 12.4247 8.92563 12.0219C8.93274 11.6191 9.02091 11.2219 9.18488 10.8539C9.34884 10.4859 9.58525 10.1547 9.88 9.88003" />
                <path d="M1 1L23 23" />
              </svg>
              {/* open eye */}
              <svg
                onClick={() => passwordEye("password")}
                viewBox="0 0 24 18"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M1 9C1 9 5 1 12 1C19 1 23 9 23 9C23 9 19 17 12 17C5 17 1 9 1 9Z" />
                <path d="M12 12C13.6569 12 15 10.6569 15 9C15 7.34315 13.6569 6 12 6C10.3431 6 9 7.34315 9 9C9 10.6569 10.3431 12 12 12Z" />
              </svg>
            </div>
            )}
            <div className="input-submit">
              <input
                type="submit"
                id="submitLogin"
                disabled={isSubmitting || !selectedUserUniqueId || !password}
                hidden
              />
              <label
                htmlFor="submitLogin"
                className={isSubmitting || !password ?"login-index" : "login-index label-submitting"}
                onClick={(e) => {
                  // Disabled submit never fires onSubmit; still show localized tooltips.
                  if (isSubmitting || !selectedUserUniqueId || !password) {
                    e.preventDefault();
                    reportLoginFieldValidity();
                  }
                }}
              >
                {isSubmitting ? t('login.loggingIn') : t('login.loginButton')}
              </label>
            </div>
            <div className="signup">
              <p>
                {t('login.dontHaveAccount')}{" "}
                <a
                  href="#"
                  onClick={handleRegisterClick}
                  style={{ cursor: isRegisterLoading ? 'wait' : 'pointer' }}
                >
                  {isRegisterLoading ? t('login.loading') : t('login.registerButton')}
                </a>
              </p>
            </div>
            <div className="reset-password">
              <p>
                {t('login.forgotPassword')}{" "}
                <a
                  // href="https://backoffice.rxbt.net/user/login"
                  href={`${getBackendUrl()}/login?tab=forgot-password`}
                  target="_blank"
                >
                  {t('login.resetPassword')}
                </a>
              </p>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default Login;
